/**
 * <session-sync> — turns a live KiTbetter session into a Shopify customer session.
 *
 * Port of the request-time gate the Hydrogen storefront ran on every document navigation
 * (`app/lib/kitbetterLoginSync.server.ts`, the `access O / login X` branch). The app webview
 * injects KiTbetter's tokens as `.kitbetter.com` cookies and expects the storefront to be
 * logged in; Hydrogen made that true by bouncing such a visitor through the OIDC round trip
 * before rendering. Liquid has no request-time hook (MIGRATION_NOTES.md #1), so it runs here.
 *
 * The round trip itself is `/customer_authentication/login?return_to=<relative path>`, which
 * calls back to the storefront and so honours return_to — see 'kitbetter-auth-url' for why
 * `/account/login` cannot be used for this. `return_to` MUST stay a relative path: an absolute
 * URL makes that entry point answer 400 "Bad request" (measured 2026-08-31).
 *
 * WHY THIS IS NOT THE SNIPPET THAT WAS REVERTED IN f4d2731
 * That one opened the gate on the *presence* of a KiTbetter cookie, which says nothing about
 * whether the token still resolves to a user — a dead token sends the visitor to an empty
 * `fanz-api.kitbetter.com/oidc/authorize` body, a white screen with no way out. The reference
 * gated on `validKitbetterAccess`, the *result* of verifying the token against api.kittor.com,
 * and that is what this listens for: <gnb-user-type> already makes that exact call on every
 * page load, so the signal costs no extra request. A visitor with no tokens is answered
 * ANONYMOUS without any network call at all and never reaches the redirect.
 *
 * Residual risk: api.kittor.com accepting the token does not strictly prove fanz-api will.
 * That is why the scope setting defaults to the app webview, where the token was injected
 * moments earlier by an app that just logged in, and why a failed trip is not retried.
 *
 * THE SKELETON
 * The round trip is a top-level navigation — it cannot be done quietly (the storefront sends
 * `x-frame-options: DENY`, and the chain is cross-origin so fetch is out), so the page it
 * returns to is a fresh render. Letting the real page paint first meant a shopper started
 * reading and then had it replaced, which read as a glitch. So the page is covered from the
 * first paint instead: an inline gate in 'sections/header.liquid' puts PENDING_CLASS on <html>
 * before the template is parsed, and this file is what takes it off — as soon as the answer is
 * anonymous, any guard here fails, or REVEAL_TIMEOUT_MS passes. On the redirect path it stays,
 * so the skeleton is what spans the navigation. The skeleton is the app webview's only: with
 * scope 'all' a visitor outside the app still gets the round trip, but uncovered, because on
 * desktop the cover read as a delay rather than as hiding a glitch.
 *
 * That inline gate opens on cookie *presence*, deliberately weaker than the condition for
 * redirecting: a wrong guess there costs a skeleton that clears a moment later, not a shopper
 * stranded mid-round-trip.
 *
 * Cost: no new network requests. One extra navigation, once per browsing session, for a
 * visitor who has a KiTbetter session but no Shopify one — after it lands, `customer` is set
 * and the element stops being rendered at all. That visitor waits on the skeleton rather than
 * seeing content early, which is the trade this makes on purpose.
 *
 * Markup contract (rendered from 'sections/header.liquid' only when `customer` is blank):
 *   data-scope         'app' — only inside the KiTbetter app webview (default)
 *                      'all' — any visitor carrying a verified KiTbetter session
 *   data-preview-theme optional theme id; the round trip rotates the cookie that holds the
 *                      preview selection, so the return path re-arms it the way
 *                      'kitbetter-auth-url' does. Empty on the published theme.
 */
const SSO_LOGIN_PATH = '/customer_authentication/login';

/** Set by the app's webview on both platforms — reference app/lib/utils.ts checkPlatform(). */
const APP_WEBVIEW_UA = /KiTPlayer_(android|iOS)/i;

/** One attempt per browsing session: a round trip that fails must not become a loop. */
const ATTEMPT_KEY = 'kitbetter:session-sync-attempted';

/**
 * Set synchronously by the inline gate in 'sections/header.liquid' so the skeleton covers the
 * page before it paints. Removing it is this file's job — see reveal().
 */
const PENDING_CLASS = 'session-sync-pending';

/**
 * The skeleton must never outlive its reason. If the verification never resolves — a stalled
 * request, a script that failed to load — the page is shown anyway, and the attempt is burned so
 * a slow session shows the skeleton once rather than on every navigation.
 */
const REVEAL_TIMEOUT_MS = 3000;

/**
 * Paths that are already part of an auth round trip. Redirecting from inside one would fight
 * the flow that is running, and /account/* is a dead end of its own (it 302s to the account
 * host, so the theme rarely renders there anyway).
 */
const SKIP_PATH_PREFIXES = ['/customer_authentication', '/account'];

/**
 * sessionStorage throws outright in some webview configurations rather than returning null,
 * and a thrown read must not stop the page. Falling back to "already attempted" is the safe
 * direction: it skips the redirect instead of risking a loop we cannot detect.
 */
const readAttempted = () => {
  try {
    return window.sessionStorage.getItem(ATTEMPT_KEY) === '1';
  } catch (error) {
    return true;
  }
};

const markAttempted = () => {
  try {
    window.sessionStorage.setItem(ATTEMPT_KEY, '1');
    return true;
  } catch (error) {
    return false;
  }
};

class SessionSync extends HTMLElement {
  connectedCallback() {
    // Every bail-out reveals: the inline gate may already have hidden the page, and it opens on a
    // weaker condition than these, so anything it let through has to be undone here.
    // The theme editor re-renders sections in place; a redirect there would fight the editor.
    if (window.Shopify && window.Shopify.designMode) return this.reveal();
    if (this.dataset.scope !== 'all' && !APP_WEBVIEW_UA.test(navigator.userAgent)) return this.reveal();
    if (SKIP_PATH_PREFIXES.some((prefix) => window.location.pathname.startsWith(prefix))) return this.reveal();
    if (readAttempted()) return this.reveal();

    this.revealTimer = window.setTimeout(() => {
      markAttempted();
      this.reveal();
    }, REVEAL_TIMEOUT_MS);

    // <gnb-user-type> applies its cached answer before it calls the API, so both arrive on this
    // event. Only the verified one is a valid gate — see the note at the top of the file.
    this.onUserType = (event) => {
      const { userType, verified } = event.detail || {};
      if (!verified) return;
      if (!userType || userType === 'ANONYMOUS') return this.reveal();
      this.start();
    };
    document.addEventListener('gnb:usertype', this.onUserType);

    // This script is deferred behind script-gnb-user-type.js, so the verified answer can already
    // have been dispatched before the listener above existed. Missing it left the skeleton up
    // until REVEAL_TIMEOUT_MS and, for a logged-in visitor, skipped the round trip altogether
    // (measured 2026-10-19: verified at 0.71s, revealed at 3.88s).
    const settled = document.documentElement.dataset.userTypeVerified;
    if (settled) this.onUserType({ detail: { userType: settled, verified: true } });
  }

  disconnectedCallback() {
    window.clearTimeout(this.revealTimer);
    if (this.onUserType) document.removeEventListener('gnb:usertype', this.onUserType);
  }

  /** Show the real page and stop waiting. Not called on the redirect path — the skeleton is what
   *  covers the navigation, and it goes away with the document. */
  reveal() {
    window.clearTimeout(this.revealTimer);
    if (this.onUserType) document.removeEventListener('gnb:usertype', this.onUserType);
    document.documentElement.classList.remove(PENDING_CLASS);
  }

  start() {
    window.clearTimeout(this.revealTimer);
    document.removeEventListener('gnb:usertype', this.onUserType);
    // A trip we cannot record would run again on the page we land on. Show the page instead.
    if (!markAttempted()) return this.reveal();

    const current = new URL(window.location.href);
    // The hash never reaches the server, so it cannot survive the trip and is dropped rather
    // than encoded into a path Shopify would have to hand back.
    current.hash = '';
    if (this.dataset.previewTheme) {
      current.searchParams.set('preview_theme_id', this.dataset.previewTheme);
    }

    const returnTo = `${current.pathname}${current.search}`;
    // replace(), not assign(): the page being left is the one we are coming back to, and a
    // history entry for it would put the visitor in a back-button loop.
    window.location.replace(`${SSO_LOGIN_PATH}?return_to=${encodeURIComponent(returnTo)}`);
  }
}

customElements.define('session-sync', SessionSync);
